import { useMemo, useState } from 'react';
import Lozenge from '@atlaskit/lozenge';
import Spinner from '@atlaskit/spinner';
import { useFlags } from '@atlaskit/flag';
import { orderBy, where } from 'firebase/firestore';
import dayjs from 'dayjs';
import SharedButton from 'shared/components/buttons/button';
import SharedTable from 'shared/components/table/table';
import { ISharedTableColumn, ISharedTableRow } from 'shared/components/table/table-interface';
import { useDialog } from 'core/providers/DialogProvider';
import { useCollection } from 'core/hooks/useCollection';
import TransportGroupsApiService from 'core/api/transport-groups/transport-groups-api.service';
import { ITransportGroup, TransportGroupStatus } from 'core/api/transport-groups/transport-groups-api-interface';
import {
  TransportGroupStatusLabels,
  canAssignBatch,
  canCancelBatch,
  canReassignBatch,
} from 'core/constants/transport-group-status';
import { showErrorFlag, showSuccessFlag } from 'core/utilities/flags-helper';
import BatchProgressDialog from './batch-progress-dialog';
import AssignBatchDialog from './assign-batch-dialog';
import ConfirmationDialog from 'modules/dialogs/confirmation-dialog';

/**
 * Batches: jobs sequenced into one journey for one driver.
 *
 * A table, like the jobs beside it. Everything a batch is asked about — how big
 * it is, how far, who has it — is a column, and the stop-by-stop detail was
 * always a dialog, so the cards this replaced bought nothing but height.
 *
 * Subscribed rather than fetched, so a batch a driver accepts on the road
 * changes here without anybody reloading. That is the one thing this screen
 * exists to report.
 *
 * There is no draft and no stale banner: a batch is written already assigned or
 * already available, so it cannot exist in nobody's hands. Not creating that
 * state is simpler than watching for it.
 *
 * Called batches rather than runs because a run is already something else in
 * this system: the scheduled logistics runs a driver signs onto.
 */

const STATUS_APPEARANCE: Record<TransportGroupStatus, 'default' | 'inprogress' | 'success' | 'removed' | 'moved'> = {
  assigned: 'inprogress',
  posted: 'inprogress',
  acknowledged: 'success',
  inProgress: 'success',
  completed: 'default',
  cancelled: 'removed',
};

const COLUMNS: ISharedTableColumn[] = [
  { key: 'reference', label: 'Batch', templateId: 'reference', width: 10 },
  { key: 'built', label: 'Built', templateId: 'built', width: 12 },
  { key: 'contents', label: 'Stops and jobs', templateId: 'contents', width: 20 },
  { key: 'distance', label: 'Distance', templateId: 'distance', width: 12 },
  { key: 'driving', label: 'Driving', templateId: 'driving', width: 12 },
  { key: 'status', label: 'Status', templateId: 'status', width: 14 },
  { key: 'driver', label: 'Driver', templateId: 'driver', width: 14 },
  { key: 'actions', label: '', templateId: 'actions', width: 18 },
];

type BatchRow = ITransportGroup & { [key: string]: unknown };

const asDate = (value?: { seconds: number } | Date): Date | undefined => {
  if (!value) return undefined;
  if (value instanceof Date) return value;
  return typeof value.seconds === 'number' ? new Date(value.seconds * 1000) : undefined;
};

interface IProps {
  /** The status the shell is narrowed to, or nothing for all of them. */
  status?: string;
}

const TransportGroupsList = ({ status }: IProps) => {
  const flags = useFlags();
  const dialog = useDialog();
  const [busy, setBusy] = useState<Record<string, boolean>>({});

  const constraints = useMemo(
    () => (status ? [where('status', '==', status), orderBy('created.at', 'desc')] : [orderBy('created.at', 'desc')]),
    [status],
  );

  const { data, loading } = useCollection<ITransportGroup>({
    apiService: TransportGroupsApiService,
    constraints,
    initialLimit: 50,
  });

  const groups = data ?? [];

  const act = async (uid: string, what: () => Promise<unknown>, success: string) => {
    setBusy((current) => ({ ...current, [uid]: true }));
    try {
      await what();
      showSuccessFlag('Done', success, flags);
    } catch (caught) {
      showErrorFlag('That did not work', (caught as Error).message, flags);
    } finally {
      setBusy((current) => ({ ...current, [uid]: false }));
    }
  };

  if (loading) {
    return (
      <div data-testid='runs-loading' className='mt-10 flex justify-center'>
        <Spinner size={40} />
      </div>
    );
  }

  const rows: ISharedTableRow<BatchRow>[] = groups.map((group) => ({
    key: group.uid,
    data: group as BatchRow,
  }));

  return (
    <div data-testid='transport-runs' className='mt-4'>
      {groups.length === 0 ? (
        <div data-testid='runs-empty' className='rounded-md bg-white p-8 text-center shadow-md'>
          <p className='headline-05'>No batches yet</p>
          <p className='body-01 mt-2 text-gray-600'>
            Batches are built from the delivery request queue: mark what you are accepting, commit, then choose to
            optimise them as a group.
          </p>
        </div>
      ) : (
        <SharedTable<BatchRow>
          columns={COLUMNS}
          rows={rows}
          customTemplates={[
            {
              id: 'reference',
              // The thing people say on the phone, so it leads the row.
              template: (row: BatchRow) => <p className='font-semibold'>{row.reference ?? '—'}</p>,
            },
            {
              id: 'built',
              template: (row: BatchRow) => {
                const built = asDate(row.created?.at);
                return (
                  <div>
                    <p>{built ? dayjs(built).format('DD/MM/YYYY') : 'Unknown'}</p>
                    <p className='text-gray-500'>{built ? dayjs(built).format('HH:mm') : ''}</p>
                  </div>
                );
              },
            },
            {
              id: 'contents',
              template: (row: BatchRow) => (
                <div>
                  <p>
                    {row.stops.length} stop{row.stops.length === 1 ? '' : 's'} · {row.jobUids.length} job
                    {row.jobUids.length === 1 ? '' : 's'}
                  </p>
                  {row.optimisation?.unassignedJobUids?.length ? (
                    <p className='text-amber-700'>
                      {row.optimisation.unassignedJobUids.length} could not be fitted in
                    </p>
                  ) : null}
                </div>
              ),
            },
            {
              id: 'distance',
              template: (row: BatchRow) => <p>{row.optimisation ? `${row.optimisation.mileage} miles` : '—'}</p>,
            },
            {
              id: 'driving',
              template: (row: BatchRow) => <p>{row.optimisation?.duration ?? '—'}</p>,
            },
            {
              id: 'status',
              template: (row: BatchRow) => (
                <span data-testid='batch-status'>
                  <Lozenge appearance={STATUS_APPEARANCE[row.status]}>
                    {TransportGroupStatusLabels[row.status]}
                  </Lozenge>
                </span>
              ),
            },
            {
              id: 'driver',
              template: (row: BatchRow) => (
                <p>{row.driver ? row.driver.fullName : <span className='text-gray-500'>—</span>}</p>
              ),
            },
            {
              id: 'actions',
              // Nowrap: wrapped onto two lines these read as two groups of
              // actions, and the second looked like it belonged to the row
              // below.
              template: (row: BatchRow) => (
                <div className='flex flex-nowrap justify-end gap-1'>
                  <SharedButton
                    type='button'
                    spacing='compact'
                    appearance='subtle'
                    label='Progress'
                    testId='view-stops'
                    onClick={() => dialog?.openDialog(<BatchProgressDialog group={row} />)}
                  />
                  {canAssignBatch(row.status) && (
                    <SharedButton
                      type='button'
                      spacing='compact'
                      appearance='default'
                      label='Assign'
                      testId='assign-run'
                      onClick={() => dialog?.openDialog(<AssignBatchDialog group={row} />)}
                    />
                  )}
                  {canReassignBatch(row.status) && (
                    <SharedButton
                      type='button'
                      spacing='compact'
                      appearance='default'
                      label='Re-assign'
                      testId='reassign-run'
                      onClick={() => dialog?.openDialog(<AssignBatchDialog group={row} />)}
                    />
                  )}
                  {canCancelBatch(row.status) && (
                    <SharedButton
                      type='button'
                      spacing='compact'
                      appearance='subtle'
                      label='Cancel'
                      testId='cancel-run'
                      isLoading={busy[row.uid]}
                      // Asked first: cancelling ends the batch and puts every
                      // job back in the pool, and it cannot be undone — you
                      // would build it again from the queue. That is too much
                      // for one click in a row of small buttons, sitting next
                      // to Assign.
                      onClick={() =>
                        dialog?.openDialog(
                          <ConfirmationDialog
                            title='Cancel this batch?'
                            textContent={`${row.reference ? `${row.reference} — ` : ''}${row.label}. Every job in it goes back to the pool for any driver to pick up, and the batch is over. Building it again means starting from the request queue.`}
                            buttonLabel='Cancel the batch'
                            dismissLabel='Keep it'
                            testId='confirm-cancel-batch'
                            action={() => {
                              dialog?.closeDialog();
                              act(
                                row.uid,
                                () => TransportGroupsApiService.cancel(row.uid),
                                'The jobs are back in the pool for any driver.',
                              );
                            }}
                          />,
                        )
                      }
                    />
                  )}
                </div>
              ),
            },
          ]}
        />
      )}
    </div>
  );
};

export default TransportGroupsList;
